import { useEffect, useState } from "react";

import type { BodyTemperatureDataPoint } from "~/api/bodyTemperature";
import {
  getBodyTemperatureData,
  getDateRange,
  getGroupByInterval,
} from "~/api/bodyTemperature";

interface UseBodyTemperatureDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseBodyTemperatureDataReturn {
  data: BodyTemperatureDataPoint[];
  metadata: {
    avg: number;
    min: number;
    max: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useBodyTemperatureData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseBodyTemperatureDataProps): UseBodyTemperatureDataReturn => {
  const [data, setData] = useState<BodyTemperatureDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    avg: number;
    min: number;
    max: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use selected date if provided, otherwise use navigation offset
      const effectiveOffset = selectedDate
        ? Math.ceil(
            (new Date().getTime() - selectedDate.getTime()) /
              (1000 * 60 * 60 * 24),
          )
        : navigationOffset;

      const dateRange = getDateRange(timeRange, effectiveOffset, selectedDate);
      const groupByInterval = getGroupByInterval(timeRange);

      const response = await getBodyTemperatureData({
        patient_id: patientId,
        group_by_interval: groupByInterval,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });
      setData(response.data);
      setMetadata({
        avg: Number((response.metadata.avg / 10).toFixed(2)),
        min: Number((response.metadata.min / 10).toFixed(2)),
        max: Number((response.metadata.max / 10).toFixed(2)),
      });
    } catch (err) {
      console.error("Error fetching body temperature data:", err);
      setError("Failed to fetch body temperature data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
